import React, { createContext, useContext, useReducer, useCallback, useEffect, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { saveSession, loadSession, docForSession } from '../lib/sessionStore';
import { toast } from 'sonner';
import { setBaselineGrid, getBaselineGuide } from '../lib/textLayout';
import { CUSTOM_SCHEME_ID, isCustomSchemeId, registerCustomSchemes } from '../lib/colors';
import { documentObjects, storyChainOf, removeFromStories, isTextType } from '../lib/storyChain';
import { storyPlainText, flowHtmlKey, flowHtmlOf, richFlowFor, stripFlowMarkersHtml } from '../lib/storyEdit';
import { withCropScaled } from '../lib/pictureCrop';

const EditorContext = createContext(null);

/** Small boolean preferences that survive reloads (e.g. the picture mini toolbar toggle). */
const readPref = (key, fallback) => {
  try { const v = window.localStorage.getItem(key); return v === null ? fallback : v === '1'; } catch { return fallback; }
};
const writePref = (key, value) => { try { window.localStorage.setItem(key, value ? '1' : '0'); } catch { /* storage unavailable */ } };
const PERSISTED_VIEW_OPTIONS = { miniToolbar: 'wp.miniToolbar', measurement: 'wp.measurement' };

export const PAGE_PRESETS = {
  letter: { width: 816, height: 1056, name: 'Letter (8.5" × 11")' },
  a4: { width: 794, height: 1123, name: 'A4 (210mm × 297mm)' },
  legal: { width: 816, height: 1344, name: 'Legal (8.5" × 14")' },
  tabloid: { width: 1056, height: 1632, name: 'Tabloid (11" × 17")' },
  a3: { width: 1123, height: 1587, name: 'A3 (297mm × 420mm)' },
  a5: { width: 559, height: 794, name: 'A5 (148mm × 210mm)' },
  postcard: { width: 576, height: 384, name: 'Postcard (6" × 4")' },
  businesscard: { width: 336, height: 192, name: 'Business Card (3.5" × 2")' },
  socialmedia: { width: 1080, height: 1080, name: 'Social Media (1080 × 1080)' },
  banner: { width: 1200, height: 300, name: 'Banner (1200 × 300)' },
};

/** 'portrait' | 'landscape' | 'square' for a page or preset. */
export const orientationOf = ({ width, height }) => (width > height ? 'landscape' : width < height ? 'portrait' : 'square');

/** The preset a page currently uses, matched in either orientation — or null for a custom size. */
export const presetKeyForPage = (page) => Object.keys(PAGE_PRESETS).find((k) => {
  const p = PAGE_PRESETS[k];
  return (p.width === page.width && p.height === page.height) || (p.width === page.height && p.height === page.width);
}) || null;

/**
 * The size to apply when a preset is picked while `page` is showing. Presets come in their natural orientation —
 * Letter portrait, Banner landscape — so Banner → Letter gives a portrait Letter (not a landscape one). The exception:
 * when the current page is a preset the user has deliberately turned (a Letter set to landscape), the new preset keeps
 * that turn, so Letter landscape → A4 stays landscape. Orientation itself is changed with the Portrait / Landscape
 * controls (Page Design ▸ Orientation, the page Properties, Page Setup).
 */
export const sizeForPreset = (preset, page) => {
  const currentKey = page ? presetKeyForPage(page) : null;
  const current = currentKey ? PAGE_PRESETS[currentKey] : null;
  const turned = !!current && orientationOf(current) !== 'square' && orientationOf(page) !== 'square' && orientationOf(current) !== orientationOf(page);
  if (!turned || orientationOf(preset) === 'square') return { width: preset.width, height: preset.height };
  const width = orientationOf(page) === 'landscape' ? Math.max(preset.width, preset.height) : Math.min(preset.width, preset.height);
  return { width, height: preset.width + preset.height - width };
};

export const createDefaultPage = (width = 816, height = 1056, name = 'Page 1') => ({
  id: uuidv4(),
  name,
  width,
  height,
  objects: [],
  background: { type: 'solid', color: '#ffffff' },
  masterId: 'default',
});

export const DEFAULT_TEXT_PROPS = {
  text: 'Type your text here',
  html: undefined,
  fontFamily: 'Inter',
  fontSize: 16,
  fontWeight: 'normal',
  fontStyle: 'normal',
  textDecoration: 'none',
  color: '#000000',
  textAlign: 'left',
  verticalAlign: 'top',
  lineHeight: 1.2,
  letterSpacing: 0,
  textScale: 100,
  tracking: 100,
  kerning: true,
  paddingTop: 4,
  paddingRight: 4,
  paddingBottom: 4,
  paddingLeft: 4,
  columns: 1,
  columnGap: 16,
  background: { type: 'none' },
  stroke: { color: '#000000', width: 0 },
  cornerRadius: 0,
  opacity: 100,
  autofit: 'none',
  textDirection: 'horizontal',
  hyphenation: false,
  textTransform: 'none',
  fontVariant: 'normal',
  textFill: null,
  textOutline: { color: '#000000', width: 0 },
  textEffects: { shadow: null, glow: null, reflection: null, bevel: null },
  dropCap: null,
  listStyle: 'none',
  indentLeft: 0,
  indentRight: 0,
  firstLineIndent: 0,
  spaceBefore: 0,
  spaceAfter: 0,
  linkNextId: null,
  linkPrevId: null,
  wrapStyle: 'none',
  wrapDistance: 8,
  hyperlink: '',
  altText: '',
  ligatures: true,
  numberStyle: 'default',
  continuedOn: false,
  continuedFrom: false,
};

/** Publication colour schemes from a saved project: `custom_schemes` (7.18) or the single legacy `custom_scheme` (7.17). */
const normalizeCustomSchemes = (p) => {
  const list = Array.isArray(p.custom_schemes) ? p.custom_schemes : (p.custom_scheme && Array.isArray(p.custom_scheme.colors) ? [{ ...p.custom_scheme, id: CUSTOM_SCHEME_ID }] : []);
  return list.filter((c) => c && Array.isArray(c.colors) && c.colors.length === 8).map((c) => ({ id: c.id && isCustomSchemeId(c.id) ? c.id : `custom:${uuidv4()}`, name: c.name || 'Custom scheme', colors: c.colors }));
};
const uniqueSchemeName = (name, schemes) => {
  const base = String(name || 'Custom scheme').trim() || 'Custom scheme';
  const taken = new Set(schemes.map((s) => s.name.toLowerCase()));
  if (!taken.has(base.toLowerCase())) return base;
  let n = 2;
  while (taken.has(`${base} ${n}`.toLowerCase())) n += 1;
  return `${base} ${n}`;
};

/**
 * Light / dark — the same rule as the landing page: an explicit choice (localStorage `wp.theme`) wins, otherwise follow
 * the system, so the theme a visitor saw while signing in is the theme the editor opens with.
 */
const readStoredTheme = () => {
  try { const v = window.localStorage.getItem('wp.theme'); if (v === 'dark' || v === 'light') return v; } catch { /* private mode */ }
  return window.matchMedia?.('(prefers-color-scheme: dark)')?.matches ? 'dark' : 'light';
};

const initialState = {
  projectId: null,
  projectName: 'Untitled Publication',
  docRevision: 0, // bumps on every document change (pages / masters / guides / scheme / name)
  savedRevision: 0, // docRevision at the last save or load → "unsaved changes" = docRevision !== savedRevision
  lastSavedAt: null,
  pages: [createDefaultPage()],
  currentPageIndex: 0,
  masters: { default: { id: 'default', name: 'Master A', objects: [] } },
  scratch: { objects: [] }, // Publisher's scratch area: objects parked beside the page — shared by every page, never printed
  theme: readStoredTheme(),

  zoom: 1,
  panX: 0,
  panY: 0,
  canvasUnit: 'in',
  viewMode: 'normal', // normal | master
  masterEditId: null, // master page being edited in Master Page view (null → the current page's master)
  layoutMode: 'single', // single | spread
  spreadStyle: 'separate', // separate | joined — Two-Page Spread shown as two pages side by side, or as one sheet with a fold line
  viewOptions: {
    boundaries: true,
    guides: true,
    fields: true,
    rulers: true,
    pageNavigation: true,
    scratchArea: true,
    graphicsManager: false,
    baselines: false,
    baselineSpacing: 14,
    panelGutter: 24, // panel margins (px) placed on either side of new fold lines (Page Design ▸ Guides ▸ Fold Lines)
    panelCenters: true, // dotted centre guide for every brochure panel (snap headings / pictures to the middle of a panel)
    miniToolbar: readPref('wp.miniToolbar', true), // floating picture mini toolbar near the selected picture (View ▸ Show)
    measurement: readPref('wp.measurement', false), // floating Measurement pane (Size ▸ Measurement / status bar)
  },

  selectedObjectIds: [],
  hoveredObjectId: null,
  editingObjectId: null,

  activeTool: 'select',
  activeShape: 'rectangle',
  ribbonTab: 'home',
  fileOpen: false,
  backstageSection: 'home', // home | new | open | info | save | save-as | print | share | export | import
  dialog: null, // { name, props }
  dialogStack: [], // dialogs waiting underneath `dialog` (e.g. Fill Effects while Colours is open on top of it)
  findPane: null, // { mode: 'find' | 'replace', query, nonce }
  wrapEditId: null, // object whose wrap points are being edited (Edit Wrap Points mode)
  cropEditId: null, // picture being cropped in place (Crop mode)
  cropAspect: null, // Crop ▸ Aspect Ratio preset id locked for the crop-handle drags ('1:1', '16:9', …; null = free)
  guideDrag: null, // a ruler guide being dragged / pulled out of a ruler: { orientation, position, along, snap, preview } — drives the on-page position tooltip
  objectDrag: null, // an object being moved / resized: { id, mode, x, y, width, height, snaps, count } — drives the on-page position / size readout
  pointEditId: null, // freeform shape whose vertices are being edited (Edit Shape ▸ Edit Points mode)
  interacting: null, // id of the object currently being dragged / resized / rotated on the canvas (hides the mini toolbar)
  pendingLink: null,
  pendingTable: null,

  gridVisible: false,
  gridSpacing: 20,
  gridPrintable: false,
  guides: [],
  snapToGuides: true,
  snapToGrid: false,
  showAlignmentGuides: true,

  schemeId: 'office',
  customSchemes: [], // publication's own colour schemes [{ id: 'custom:<uuid>', name, colors: [8 × { name, hex }] }] (Page Design ▸ Schemes ▸ From Picture…)
  fontScheme: null, // { id, name, heading, body } — the font scheme last applied to this publication (Page Design ▸ Schemes ▸ Fonts)
  sharedFrom: null, // { name, user_id, project_id, at } — set on a copy that arrived through a share link ("Shared by Olly")
  textSettings: { fontFamily: 'Inter', fontSize: 16 },
  shapeSettings: { fill: { type: 'solid', color: '#7c3aed' }, stroke: { color: '#000000', width: 0 }, cornerRadius: 0 },
  formatPainter: null,

  history: [],
  historyIndex: -1,
  maxHistorySize: 50,

  leftPanelTab: 'pages',
  rightPanelTab: 'properties',
  showPhotoEditor: false,
  photoEditorImage: null,
  showImageGallery: false,
  galleryInsertMode: 'insert', // insert | replace | background | fill

  clipboard: [],
  pageClipboard: null, // { page, sourceName } — Copy Page / Paste Page (shared by all open publications)
  autosave: readPref('wp.autosave', true), // quietly save cloud publications every minute
  autosaveStatus: null, // { at, count, error? } — last autosave run
  textFlow: {}, // textboxId -> overflow text (runtime only)
  fontPreview: null, // { ids, family } — hovering a font in the picker previews it on these objects (visual only, runtime only)

  // Open publications (document tabs). The active tab's document is the live state above; the other tabs keep a
  // snapshot of their document (`doc`) until they are switched to. A publication only closes when its tab is closed.
  docTabs: [{ id: 'tab-1', doc: null }],
  activeTabId: 'tab-1',

  isLoading: false,
  isSaving: false,
  businessInfo: null,
};

/* ---------------- document tabs ---------------- */
/** The per-publication part of the state — everything a tab has to remember while another tab is active. */
export const DOC_KEYS = [
  'projectId', 'projectName', 'docRevision', 'savedRevision', 'lastSavedAt', 'pages', 'currentPageIndex', 'masters', 'scratch',
  'zoom', 'panX', 'panY', 'canvasUnit', 'viewMode', 'masterEditId', 'layoutMode', 'spreadStyle', 'gridVisible', 'gridSpacing', 'gridPrintable',
  'guides', 'schemeId', 'customSchemes', 'fontScheme', 'sharedFrom', 'history', 'historyIndex', 'historyDirty', 'selectedObjectIds',
];
export const pickDoc = (state) => Object.fromEntries(DOC_KEYS.map((k) => [k, state[k]]));

// Booklet pairing for the two-page spread (Publisher): page 1 alone on the right, then (2,3) (4,5) …; an even last page
// sits alone on the left. Pure helpers live in lib/spreads.js (shared with Export / Print); re-exported for convenience.
export { spreadPartner, spreadRows } from '../lib/spreads';
/** Modes and transient UI that must not leak from one publication into another. */
const DOC_RUNTIME_RESET = {
  editingObjectId: null, hoveredObjectId: null, textFlow: {}, fontPreview: null, wrapEditId: null, cropEditId: null, cropAspect: null, guideDrag: null, objectDrag: null, pointEditId: null,
  interacting: null, pendingLink: null, pendingTable: null, findPane: null, dialog: null, dialogStack: [], formatPainter: null,
  activeTool: 'select', showPhotoEditor: false, photoEditorImage: null, showImageGallery: false, isSaving: false,
};
/** Anything on the pages / masters, or more than one page. */
export const docHasContent = (doc) => (doc.pages || []).length > 1 || (doc.pages || []).some((pg) => (pg.objects || []).length > 0) || Object.values(doc.masters || {}).some((m) => (m.objects || []).length > 0) || (doc.scratch?.objects || []).length > 0;
/** A never-touched, never-saved, empty publication (reused instead of opening yet another tab). */
export const isPristineDoc = (doc) => !doc.projectId && !doc.docRevision && !doc.savedRevision && !docHasContent(doc);
export const docIsDirty = (doc) => !!doc && doc.docRevision !== doc.savedRevision;
const stashActiveDoc = (state) => state.docTabs.map((t) => (t.id === state.activeTabId ? { ...t, doc: pickDoc(state) } : t));
const restoreDoc = (state, doc) => {
  const d = { ...doc, history: Array.isArray(doc.history) ? doc.history : [], historyIndex: Array.isArray(doc.history) ? doc.historyIndex ?? doc.history.length - 1 : -1 };
  registerCustomSchemes(d.customSchemes || []);
  return { ...state, ...DOC_RUNTIME_RESET, ...d, selectedObjectIds: [], loadSeq: (state.loadSeq || 0) + 1, fileOpen: false, ribbonTab: d.viewMode === 'master' ? 'master-page' : 'home' };
};
const blankProjectPayload = () => ({ id: null, name: 'Untitled Publication', pages: [createDefaultPage()], scratch: { objects: [] }, canvas_unit: 'in', grid_visible: false, grid_spacing: 20, guides: [] });
const newTabId = () => `tab-${uuidv4()}`;

const clonePage = (page) => ({ ...page, objects: page.objects.map((o) => ({ ...o })) });

/* ---------------- master pages ---------------- */
/** Masters in display order (A, B, C …) — the order they were created in. */
export const masterList = (masters) => Object.values(masters || {});
/** Publisher labels master pages with letters: A, B, … Z, AA, AB … */
export const masterLetter = (masters, id) => {
  const i = masterList(masters).findIndex((m) => m.id === id);
  if (i < 0) return '?';
  let n = i, s = '';
  do { s = String.fromCharCode(65 + (n % 26)) + s; n = Math.floor(n / 26) - 1; } while (n >= 0);
  return s;
};
const firstMasterId = (masters) => masterList(masters)[0]?.id || 'default';
/** The master a page shows in Normal view (null when the page ignores its master). */
export const pageMasterOf = (state, page) => (!page || page.masterId === 'none' ? null : state.masters[page.masterId] || state.masters[firstMasterId(state.masters)] || null);
/**
 * The master edited in Master Page view: `state.masterEditId` when set (Pages pane / ribbon choice), otherwise the
 * current page's master, otherwise the first one. Also used as the fallback for pages without a master.
 */
export const activeMasterOf = (state) => {
  const m = state.masters || {};
  if (state.viewMode === 'master' && state.masterEditId && m[state.masterEditId]) return m[state.masterEditId];
  return pageMasterOf(state, state.pages[state.currentPageIndex]) || m[firstMasterId(m)] || null;
};
const nextMasterName = (masters) => {
  const taken = new Set(masterList(masters).map((x) => x.name.toLowerCase()));
  for (let i = 0; i < 26 * 27; i++) {
    let n = i, s = '';
    do { s = String.fromCharCode(65 + (n % 26)) + s; n = Math.floor(n / 26) - 1; } while (n >= 0);
    if (!taken.has(`master ${s}`.toLowerCase())) return `Master ${s}`;
  }
  return `Master ${Object.keys(masters).length + 1}`;
};

/* ---------------- scratch area ----------------
 * Publisher keeps a grey "scratch area" around the page. Anything dropped there belongs to the publication, not to a page:
 * it stays put while you flip pages and is never printed. We model it as `state.scratch.objects` and decide membership by
 * geometry after every edit: an object whose box lies *entirely* outside the current page is in the scratch area; one that
 * touches the page belongs to that page. Dragging therefore moves objects between the two without any extra UI. */
export const scratchObjectsOf = (state) => state.scratch?.objects || [];
/** Entirely outside the page rectangle (axis-aligned box; rotation ignored, like Publisher). */
export const isOffPage = (o, page) => {
  if (!o || !page) return false;
  const w = Math.max(0, o.width || 0), h = Math.max(0, o.height || 0);
  return o.x + w <= 0 || o.y + h <= 0 || o.x >= page.width || o.y >= page.height;
};
const splitScratch = (objects, page) => {
  const onPage = [], off = [];
  objects.forEach((o) => (isOffPage(o, page) ? off : onPage).push(o));
  return { onPage, off };
};

const withCurrentPage = (state, fn) => {
  if (state.viewMode === 'master') {
    const master = activeMasterOf(state);
    const updated = fn({ ...master, objects: [...master.objects] });
    return { ...state, masters: { ...state.masters, [master.id]: { ...master, objects: updated.objects } } };
  }
  const page = state.pages[state.currentPageIndex];
  const scratch = scratchObjectsOf(state);
  // the reducer cases see one list: the page's objects followed by the scratch area's
  const combined = clonePage(page);
  combined.objects = [...combined.objects, ...scratch.map((o) => ({ ...o }))];
  const updated = fn(combined);
  const { onPage, off } = splitScratch(updated.objects || [], page);
  const newPages = [...state.pages];
  newPages[state.currentPageIndex] = { ...updated, objects: onPage };
  const sameScratch = off.length === scratch.length && off.every((o, i) => o === scratch[i] || JSON.stringify(o) === JSON.stringify(scratch[i]));
  return { ...state, pages: newPages, scratch: sameScratch ? (state.scratch || { objects: [] }) : { ...(state.scratch || {}), objects: off } };
};

const getActiveObjects = (state) => {
  if (state.viewMode === 'master') return activeMasterOf(state)?.objects || [];
  const scratch = scratchObjectsOf(state);
  const own = state.pages[state.currentPageIndex]?.objects || [];
  return scratch.length ? [...own, ...scratch] : own;
};

/**
 * Apply `fn(object) → object` to every object on every page and in the scratch area (stories link across pages, so
 * link bookkeeping must see the whole publication). Untouched pages keep their identity.
 */
const mapDocObjects = (state, fn) => {
  let changed = false;
  const mapList = (list) => {
    let dirty = false;
    const out = list.map((o) => { const n = fn(o); if (n !== o) dirty = true; return n; });
    return dirty ? out : list;
  };
  const pages = state.pages.map((pg) => {
    const objects = mapList(pg.objects || []);
    if (objects === pg.objects) return pg;
    changed = true;
    return { ...pg, objects };
  });
  const scratchList = scratchObjectsOf(state);
  const scratchObjects = mapList(scratchList);
  if (scratchObjects !== scratchList) changed = true;
  if (!changed) return state;
  return { ...state, pages, scratch: scratchObjects === scratchList ? state.scratch : { ...(state.scratch || {}), objects: scratchObjects } };
};
/** Patch the properties of several objects anywhere in the publication: Map<id, props> or { id: props }. */
const patchDocProps = (state, patches) => {
  const get = patches instanceof Map ? (id) => patches.get(id) : (id) => patches[id];
  return mapDocObjects(state, (o) => { const np = get(o.id); return np ? { ...o, properties: np } : o; });
};

const regenerateIds = (obj) => ({
  ...obj,
  id: uuidv4(),
  properties: obj.properties ? { ...obj.properties, linkNextId: null, linkPrevId: null } : obj.properties,
  children: obj.children ? obj.children.map(regenerateIds) : undefined,
});

/**
 * Clone a set of objects with fresh ids while keeping the text links *between copied objects* (a whole linked story
 * copied together stays linked; links to objects that were not copied are dropped).
 */
export const cloneObjectsKeepingLinks = (objs) => {
  const idMap = new Map(objs.map((o) => [o.id, uuidv4()]));
  return objs.map((o) => {
    const c = regenerateIds(o);
    c.id = idMap.get(o.id);
    if (o.properties && (o.properties.linkNextId || o.properties.linkPrevId)) {
      c.properties = { ...c.properties, linkNextId: idMap.get(o.properties.linkNextId) || null, linkPrevId: idMap.get(o.properties.linkPrevId) || null };
    }
    return c;
  });
};
/** A deep copy of a page with new ids (objects keep their links to each other). */
export const clonePageKeepingLinks = (page, name) => ({
  ...page,
  id: uuidv4(),
  name: name || page.name,
  background: page.background ? { ...page.background } : page.background,
  objects: cloneObjectsKeepingLinks(page.objects || []),
});
/** Name for a pasted page: the source page's own name when it is free here, otherwise the next "Page N". */
const pastedPageName = (name, pages) => {
  const taken = new Set(pages.map((p) => String(p.name || '').toLowerCase()));
  if (name && !taken.has(String(name).toLowerCase())) return name;
  let n = pages.length + 1;
  while (taken.has(`page ${n}`)) n += 1;
  return `Page ${n}`;
};
/** Shift a pasted group so it lands inside the page (a Letter selection pasted onto a postcard stays reachable). */
const fitGroupIntoPage = (objs, page) => {
  if (!objs.length) return objs;
  const minX = Math.min(...objs.map((o) => o.x)), minY = Math.min(...objs.map((o) => o.y));
  const maxX = Math.max(...objs.map((o) => o.x + o.width)), maxY = Math.max(...objs.map((o) => o.y + o.height));
  let dx = 0, dy = 0;
  if (maxX > page.width) dx = Math.max(-minX, page.width - maxX);
  if (maxY > page.height) dy = Math.max(-minY, page.height - maxY);
  if (minX + dx < 0) dx = -minX;
  if (minY + dy < 0) dy = -minY;
  return dx || dy ? objs.map((o) => ({ ...o, x: o.x + dx, y: o.y + dy })) : objs;
};
/**
 * Apply a document change to a background tab's snapshot the way `historyDispatch` does for the live document: record
 * the document *before* the change as a history entry (unless the top entry already is that document — the same rule
 * as PUSH_HISTORY), apply `change(doc)`, and leave the result "dirty" against that entry so Undo in that tab steps back
 * to it (and Redo returns). Bumps the revision so the tab shows its unsaved dot and autosave picks it up.
 */
const changeDocWithHistory = (doc, change) => {
  const before = { pages: JSON.parse(JSON.stringify(doc.pages)), masters: JSON.parse(JSON.stringify(doc.masters || {})), scratch: JSON.parse(JSON.stringify(doc.scratch || { objects: [] })), selectedObjectIds: [], customSchemes: doc.customSchemes };
  const history = (doc.history || []).slice(0, (doc.historyIndex ?? (doc.history || []).length - 1) + 1);
  const top = history[history.length - 1];
  if (!top || JSON.stringify(top.pages) !== JSON.stringify(before.pages) || JSON.stringify(top.masters || {}) !== JSON.stringify(before.masters) || JSON.stringify(top.scratch || { objects: [] }) !== JSON.stringify(before.scratch)) {
    history.push(before);
    if (history.length > 50) history.shift();
  }
  return { ...change(doc), history, historyIndex: history.length - 1, historyDirty: true, docRevision: (doc.docRevision || 0) + 1 };
};

/**
 * Undo / Redo normally clear the selection. While "Edit Wrap Points" / "Edit Points" is active, keep its object selected
 * (if the restored document still has it in a usable state) so undoing a drag does not throw the user out of the mode.
 */
/**
 * Colour schemes recorded with a history snapshot (see PUSH_HISTORY): restoring them re-registers SCHEME_PRESETS and keeps
 * the active scheme id valid. Snapshots from before 7.18 carry none → the current list stays.
 */
const restoreSchemes = (state, entry) => {
  if (!entry || !Array.isArray(entry.customSchemes) || entry.customSchemes === state.customSchemes) return {};
  registerCustomSchemes(entry.customSchemes);
  const stillThere = !isCustomSchemeId(state.schemeId) || entry.customSchemes.some((c) => c.id === state.schemeId);
  return { customSchemes: entry.customSchemes, schemeId: stillThere ? state.schemeId : 'office' };
};

const keepWrapEditSelection = (prev, restored) => {
  const id = prev.wrapEditId || prev.pointEditId;
  if (!id) return restored;
  const o = getActiveObjects(restored).find((x) => x.id === id);
  if (!o) return restored;
  if (prev.wrapEditId) {
    const pts = o.properties?.wrapPoints;
    return Array.isArray(pts) && pts.length >= 3 ? { ...restored, selectedObjectIds: [o.id] } : restored;
  }
  return o.shapeType === 'freeform' && o.properties?.freeform?.paths ? { ...restored, selectedObjectIds: [o.id] } : restored;
};

/**
 * Selection after Undo / Redo: keep whatever is currently selected as long as those objects still exist in the restored
 * document, otherwise fall back to the selection recorded with the snapshot (Publisher keeps the selection so you can
 * keep editing without re-clicking).
 */
const selectionAfterHistory = (prev, restoredDoc, snapshotIds) => {
  // the restored document may have fewer pages than the current one (Undo of Add / Paste Page) → stay on a real page
  const restored = { ...restoredDoc, currentPageIndex: Math.max(0, Math.min(restoredDoc.currentPageIndex || 0, restoredDoc.pages.length - 1)) };
  const liveObjects = getActiveObjects(restored);
  const live = new Set(liveObjects.map((o) => o.id));
  const keep = (prev.selectedObjectIds || []).filter((id) => live.has(id));
  if (keep.length) return { ...restored, selectedObjectIds: keep };
  // Nothing selected survived (e.g. Undo of a Delete, Redo of an Insert): select what the step changed or brought back
  // on this page — that is the object the user is working on.
  const before = new Map(getActiveObjects(prev).map((o) => [o.id, JSON.stringify(o)]));
  const changed = liveObjects.filter((o) => before.get(o.id) !== JSON.stringify(o)).map((o) => o.id);
  if (changed.length && changed.length <= 50) return { ...restored, selectedObjectIds: changed };
  return { ...restored, selectedObjectIds: (snapshotIds || []).filter((id) => live.has(id)) };
};

const editorReducer = (state, action) => {
  switch (action.type) {
    case 'SET_THEME':
      return { ...state, theme: action.payload };

    case 'SET_PROJECT': {
      const p = action.payload;
      const customSchemes = normalizeCustomSchemes(p);
      registerCustomSchemes(customSchemes);
      const masters = p.masters && Object.keys(p.masters).length ? p.masters : { default: { id: 'default', name: 'Master A', objects: [] } };
      Object.keys(masters).forEach((k) => { if (!masters[k].id) masters[k] = { ...masters[k], id: k }; if (!Array.isArray(masters[k].objects)) masters[k] = { ...masters[k], objects: [] }; });
      const firstMaster = firstMasterId(masters);
      // pages keep 'none' (ignore master); a missing / unknown master id falls back to the first master
      const pages = (p.pages && p.pages.length ? p.pages : [createDefaultPage()]).map((pg) => ({ ...pg, masterId: pg.masterId === 'none' ? 'none' : (pg.masterId && masters[pg.masterId] ? pg.masterId : firstMaster), objects: pg.objects || [] }));
      return {
        ...state,
        projectId: p.id ?? null,
        projectName: p.name || 'Untitled Publication',
        pages,
        masters,
        scratch: { objects: Array.isArray(p.scratch?.objects) ? p.scratch.objects : [] },
        currentPageIndex: 0,
        canvasUnit: p.canvas_unit || 'in',
        layoutMode: p.layout_mode === 'spread' ? 'spread' : 'single', // the publication remembers Single Page / Two-Page Spread
        spreadStyle: p.spread_style === 'joined' ? 'joined' : 'separate', // … and how the spread is displayed (separate pages / joined sheet)
        gridVisible: p.grid_visible || false,
        gridSpacing: p.grid_spacing || 20,
        guides: p.guides || [],
        selectedObjectIds: [],
        editingObjectId: null,
        viewMode: 'normal',
        masterEditId: null,
        textFlow: {},
        history: [],
        historyIndex: -1,
        historyDirty: false,
        schemeId: isCustomSchemeId(p.scheme_id) && !customSchemes.some((c) => c.id === p.scheme_id) ? 'office' : (p.scheme_id || state.schemeId || 'office'),
        customSchemes,
        fontScheme: p.font_scheme && p.font_scheme.heading && p.font_scheme.body ? { id: p.font_scheme.id || null, name: p.font_scheme.name || '', heading: p.font_scheme.heading, body: p.font_scheme.body } : null,
        sharedFrom: p.shared_from && p.shared_from.name ? p.shared_from : null,
        fileOpen: false,
        ribbonTab: 'home',
        zoom: 1,
        loadSeq: (state.loadSeq || 0) + 1,
        docRevision: 0,
        // a new publication that already has content (template, import, project file) is unsaved work → it shows as
        // "unsaved" right away and asks before closing; a blank one is clean until edited
        savedRevision: p.id || !docHasContent({ pages, masters }) ? 0 : -1,
        lastSavedAt: p.updated_at || null,
      };
    }

    case 'SET_PROJECT_NAME':
      return { ...state, projectName: action.payload };
    case 'SET_PROJECT_ID':
      return { ...state, projectId: action.payload };
    case 'MARK_SAVED': {
      // The document has been persisted (cloud save or project-file download). payload: ISO string, or
      // { at, revision } — `revision` is the docRevision that was actually sent, so edits made meanwhile stay unsaved.
      const pl = action.payload && typeof action.payload === 'object' ? action.payload : { at: action.payload };
      const revision = typeof pl.revision === 'number' ? Math.min(pl.revision, state.docRevision) : state.docRevision;
      // never step backwards when an older in-flight save completes after a newer one
      return { ...state, savedRevision: Math.max(revision, state.savedRevision), lastSavedAt: pl.at || new Date().toISOString() };
    }

    case 'SET_ZOOM':
      return { ...state, zoom: Math.max(0.1, Math.min(6, action.payload)) };
    case 'SET_PAN':
      return { ...state, panX: action.payload.x, panY: action.payload.y };
    case 'SET_CANVAS_UNIT':
      return { ...state, canvasUnit: action.payload };
    case 'SET_VIEW_MODE': {
      // Entering Master Page view edits the current page's master (or the first one); leaving it returns the ribbon to
      // Home if the contextual Master Page tab was showing. Payload may also be { mode, masterId }.
      const mode = typeof action.payload === 'string' ? action.payload : action.payload?.mode;
      const wanted = typeof action.payload === 'object' ? action.payload?.masterId : null;
      if (mode === 'master') {
        const id = (wanted && state.masters[wanted]) ? wanted : (pageMasterOf(state, state.pages[state.currentPageIndex])?.id || firstMasterId(state.masters));
        return { ...state, viewMode: 'master', masterEditId: id, selectedObjectIds: [], editingObjectId: null, ribbonTab: 'master-page' };
      }
      return { ...state, viewMode: 'normal', masterEditId: null, selectedObjectIds: [], editingObjectId: null, ribbonTab: state.ribbonTab === 'master-page' ? 'home' : state.ribbonTab };
    }
    case 'EDIT_MASTER': {
      const id = state.masters[action.payload?.id] ? action.payload.id : (pageMasterOf(state, state.pages[state.currentPageIndex])?.id || firstMasterId(state.masters));
      return { ...state, viewMode: 'master', masterEditId: id, selectedObjectIds: [], editingObjectId: null, ribbonTab: 'master-page' };
    }
    case 'ADD_MASTER': {
      // payload: { name?, duplicateOf?, edit? } — a new (optionally duplicated) master page; `edit` opens it right away
      const src = action.payload?.duplicateOf ? state.masters[action.payload.duplicateOf] : null;
      const id = uuidv4();
      const name = (action.payload?.name || '').trim() || (src ? `${src.name} copy` : nextMasterName(state.masters));
      const master = { id, name, objects: src ? src.objects.map(regenerateIds) : [] };
      const masters = { ...state.masters, [id]: master };
      if (action.payload?.edit || state.viewMode === 'master') return { ...state, masters, viewMode: 'master', masterEditId: id, selectedObjectIds: [], editingObjectId: null, ribbonTab: 'master-page' };
      return { ...state, masters };
    }
    case 'RENAME_MASTER': {
      const m = state.masters[action.payload.id];
      const name = (action.payload.name || '').trim();
      if (!m || !name || m.name === name) return state;
      return { ...state, masters: { ...state.masters, [m.id]: { ...m, name } } };
    }
    case 'DELETE_MASTER': {
      // Pages using the deleted master fall back to the first remaining one (Publisher asks; we keep it simple and undoable).
      const id = action.payload?.id;
      if (!state.masters[id] || masterList(state.masters).length <= 1) return state;
      const masters = { ...state.masters };
      delete masters[id];
      const fallback = firstMasterId(masters);
      const pages = state.pages.map((p) => (p.masterId === id ? { ...p, masterId: fallback } : p));
      const masterEditId = state.masterEditId === id ? fallback : state.masterEditId;
      return { ...state, masters, pages, masterEditId, selectedObjectIds: state.viewMode === 'master' && state.masterEditId === id ? [] : state.selectedObjectIds, editingObjectId: state.viewMode === 'master' && state.masterEditId === id ? null : state.editingObjectId };
    }
    case 'APPLY_MASTER': {
      // payload: { masterId: id | 'none', pageIndex?: number, all?: boolean }
      const { masterId, pageIndex, all } = action.payload || {};
      if (masterId !== 'none' && !state.masters[masterId]) return state;
      const idx = pageIndex ?? state.currentPageIndex;
      const pages = state.pages.map((p, i) => ((all || i === idx) && p.masterId !== masterId ? { ...p, masterId } : p));
      return pages.some((p, i) => p !== state.pages[i]) ? { ...state, pages } : state;
    }
    case 'SET_LAYOUT_MODE':
      return { ...state, layoutMode: action.payload };
    case 'SET_SPREAD_STYLE':
      return { ...state, spreadStyle: action.payload === 'joined' ? 'joined' : 'separate' };
    case 'SET_VIEW_OPTION':
      if (PERSISTED_VIEW_OPTIONS[action.payload.key]) writePref(PERSISTED_VIEW_OPTIONS[action.payload.key], !!action.payload.value);
      return { ...state, viewOptions: { ...state.viewOptions, [action.payload.key]: action.payload.value } };
    case 'SET_INTERACTING':
      return state.interacting === action.payload ? state : { ...state, interacting: action.payload };
    case 'SET_RIBBON_TAB':
      return { ...state, ribbonTab: action.payload };
    case 'SET_FILE_OPEN': {
      // payload: boolean | { open: boolean, section?: string }
      const p = action.payload;
      if (p && typeof p === 'object') {
        return { ...state, fileOpen: !!p.open, backstageSection: p.section || state.backstageSection, dialog: p.open ? null : state.dialog, dialogStack: p.open ? [] : state.dialogStack };
      }
      return { ...state, fileOpen: !!p, backstageSection: p ? 'home' : state.backstageSection, dialog: p ? null : state.dialog, dialogStack: p ? [] : state.dialogStack };
    }
    case 'SET_BACKSTAGE_SECTION':
      return { ...state, backstageSection: action.payload };
    case 'OPEN_DIALOG': {
      const next = { name: action.payload.name, props: action.payload.props || {} };
      // `stack`: open on top of the dialog that is already open (Colours from a Fill Effects stop, a Font dialog swatch…)
      // — the one underneath stays mounted, keeps its state, and comes back when this one closes.
      if (action.payload.stack && state.dialog && state.dialog.name !== next.name && (state.dialogStack || []).length < 3) {
        return { ...state, dialog: next, dialogStack: [...(state.dialogStack || []), state.dialog] };
      }
      return { ...state, dialog: next, dialogStack: [] };
    }
    case 'CLOSE_DIALOG': {
      const stack = state.dialogStack || [];
      if (stack.length) return { ...state, dialog: stack[stack.length - 1], dialogStack: stack.slice(0, -1) };
      return { ...state, dialog: null };
    }
    case 'SET_CROP_EDIT':
      // entering / leaving crop mode (or moving it to another picture) starts with a free aspect ratio
      return state.cropEditId === action.payload ? state : { ...state, cropEditId: action.payload, cropAspect: null, wrapEditId: action.payload ? null : state.wrapEditId, pointEditId: action.payload ? null : state.pointEditId };
    case 'SET_CROP_ASPECT':
      return state.cropAspect === action.payload ? state : { ...state, cropAspect: action.payload || null };
    case 'SET_GUIDE_DRAG':
      return state.guideDrag === action.payload ? state : { ...state, guideDrag: action.payload || null };
    case 'SET_OBJECT_DRAG':
      return state.objectDrag === action.payload ? state : { ...state, objectDrag: action.payload || null };
    case 'SET_WRAP_EDIT':
      return state.wrapEditId === action.payload ? state : { ...state, wrapEditId: action.payload, pointEditId: action.payload ? null : state.pointEditId, cropEditId: action.payload ? null : state.cropEditId };
    case 'SET_POINT_EDIT':
      return state.pointEditId === action.payload ? state : { ...state, pointEditId: action.payload, wrapEditId: action.payload ? null : state.wrapEditId };
    case 'SET_FIND_PANE':
      // { mode: 'find' | 'replace', query? } or null to close
      return { ...state, findPane: action.payload ? { mode: action.payload.mode || 'find', query: action.payload.query, nonce: (state.findPane?.nonce || 0) + 1 } : null };
    case 'SET_SCHEME':
      return { ...state, schemeId: action.payload };
    case 'ADD_CUSTOM_SCHEME': {
      // payload: { id?, name, colors[8] } — adds a publication scheme and makes it the active one
      const sc = action.payload;
      if (!sc || !Array.isArray(sc.colors) || sc.colors.length !== 8) return state;
      const id = sc.id && isCustomSchemeId(sc.id) ? sc.id : `custom:${uuidv4()}`;
      const customSchemes = [...state.customSchemes.filter((c) => c.id !== id), { id, name: uniqueSchemeName(sc.name || 'Custom scheme', state.customSchemes), colors: sc.colors }];
      registerCustomSchemes(customSchemes);
      return { ...state, customSchemes, schemeId: id };
    }
    case 'UPDATE_CUSTOM_SCHEME': {
      // payload: { id, name?, colors? }
      const { id, ...patch } = action.payload || {};
      if (!state.customSchemes.some((c) => c.id === id)) return state;
      const customSchemes = state.customSchemes.map((c) => (c.id === id ? { ...c, ...(patch.name !== undefined ? { name: String(patch.name).trim() || c.name } : {}), ...(Array.isArray(patch.colors) && patch.colors.length === 8 ? { colors: patch.colors } : {}) } : c));
      registerCustomSchemes(customSchemes);
      return { ...state, customSchemes };
    }
    case 'DUPLICATE_CUSTOM_SCHEME': {
      const src = state.customSchemes.find((c) => c.id === action.payload);
      if (!src) return state;
      const copy = { id: `custom:${uuidv4()}`, name: uniqueSchemeName(`${src.name} copy`, state.customSchemes), colors: src.colors.map((c) => ({ ...c })) };
      const customSchemes = [...state.customSchemes, copy];
      registerCustomSchemes(customSchemes);
      return { ...state, customSchemes, schemeId: copy.id };
    }
    case 'DELETE_CUSTOM_SCHEME': {
      if (!state.customSchemes.some((c) => c.id === action.payload)) return state;
      const customSchemes = state.customSchemes.filter((c) => c.id !== action.payload);
      registerCustomSchemes(customSchemes);
      return { ...state, customSchemes, schemeId: state.schemeId === action.payload ? 'office' : state.schemeId };
    }
    case 'SET_FONT_SCHEME':
      // payload: { id, name, heading, body } or null — remembered so the next font scheme knows which families are heading / body
      return { ...state, fontScheme: action.payload && action.payload.heading && action.payload.body ? action.payload : null };
    case 'REPLACE_DOCUMENT_CONTENT':
    case 'PREVIEW_DOCUMENT_CONTENT':
      // payload: { pages?, masters? } — whole-document rewrites (font substitution, recolouring); one undoable step.
      // PREVIEW_* is the same rewrite without a history push: live previews (Edit Colour Scheme) record one snapshot when they
      // start and rely on Undo's "dirty document" handling afterwards.
      return { ...state, pages: action.payload.pages || state.pages, masters: action.payload.masters || state.masters };
    case 'SET_BUSINESS_INFO':
      return { ...state, businessInfo: action.payload };
    case 'SET_FORMAT_PAINTER':
      return { ...state, formatPainter: action.payload };

    case 'SET_ACTIVE_TOOL':
      return { ...state, activeTool: action.payload, selectedObjectIds: action.payload !== 'select' ? [] : state.selectedObjectIds, editingObjectId: null };
    case 'SET_ACTIVE_SHAPE':
      return { ...state, activeShape: action.payload };

    case 'SELECT_OBJECT': {
      if (action.payload.multi) {
        const exists = state.selectedObjectIds.includes(action.payload.id);
        return { ...state, selectedObjectIds: exists ? state.selectedObjectIds.filter((i) => i !== action.payload.id) : [...state.selectedObjectIds, action.payload.id] };
      }
      return { ...state, selectedObjectIds: [action.payload.id], editingObjectId: state.editingObjectId === action.payload.id ? state.editingObjectId : null };
    }
    case 'SELECT_OBJECTS':
      return { ...state, selectedObjectIds: action.payload, editingObjectId: null };
    case 'SELECT_ALL':
      return { ...state, selectedObjectIds: getActiveObjects(state).filter((o) => o.visible !== false && !o.locked).map((o) => o.id), editingObjectId: null };
    case 'DESELECT_ALL':
      return { ...state, selectedObjectIds: [], editingObjectId: null };
    case 'SET_HOVERED_OBJECT':
      return { ...state, hoveredObjectId: action.payload };
    case 'SET_EDITING':
      return { ...state, editingObjectId: action.payload, selectedObjectIds: action.payload ? [action.payload] : state.selectedObjectIds };
    case 'SET_PENDING_LINK':
      return { ...state, pendingLink: action.payload };
    case 'SET_PENDING_TABLE':
      return { ...state, pendingTable: action.payload };

    // ---------- pages ----------
    case 'ADD_PAGE': {
      const cur = state.pages[state.currentPageIndex];
      const at = action.payload?.index ?? state.pages.length;
      const newPage = createDefaultPage(cur.width, cur.height, `Page ${state.pages.length + 1}`);
      newPage.masterId = cur.masterId || 'default';
      if (action.payload?.duplicate) {
        newPage.objects = cloneObjectsKeepingLinks(cur.objects); // a linked story on the page stays linked on the copy
        newPage.background = { ...cur.background };
      }
      const pages = [...state.pages];
      pages.splice(at, 0, newPage);
      return { ...state, pages, currentPageIndex: at, selectedObjectIds: [] };
    }
    case 'DELETE_PAGE': {
      if (state.pages.length <= 1) return state;
      const goneIds = (state.pages[action.payload]?.objects || []).map((o) => o.id);
      const patches = goneIds.length ? removeFromStories(documentObjects(state.pages, state.scratch), goneIds) : new Map();
      const repaired = patches.size ? patchDocProps(state, patches) : state;
      const newPages = repaired.pages.filter((_, i) => i !== action.payload);
      const newIndex = Math.min(state.currentPageIndex, newPages.length - 1);
      return { ...repaired, pages: newPages, currentPageIndex: newIndex, selectedObjectIds: [] };
    }
    case 'MOVE_PAGE': {
      const { from, to } = action.payload;
      if (from === to || from < 0 || to < 0 || from >= state.pages.length || to >= state.pages.length) return state;
      const pages = [...state.pages];
      const [moved] = pages.splice(from, 1);
      pages.splice(to, 0, moved);
      return { ...state, pages, currentPageIndex: to };
    }
    case 'SET_CURRENT_PAGE':
      return { ...state, currentPageIndex: Math.max(0, Math.min(action.payload, state.pages.length - 1)), selectedObjectIds: [], editingObjectId: null };
    case 'UPDATE_PAGE': {
      const newPages = [...state.pages];
      newPages[action.payload.index] = { ...newPages[action.payload.index], ...action.payload.data };
      return { ...state, pages: newPages };
    }
    case 'UPDATE_ALL_PAGES_SIZE': {
      const { width, height } = action.payload;
      return { ...state, pages: state.pages.map((p) => ({ ...p, width, height })) };
    }
    case 'UPDATE_MASTER': {
      const m = state.masters[action.payload.id] || activeMasterOf(state);
      if (!m) return state;
      return { ...state, masters: { ...state.masters, [m.id]: { ...m, ...action.payload.data } } };
    }

    // ---------- objects ----------
    case 'ADD_OBJECT':
      return {
        ...withCurrentPage(state, (page) => {
          const newObject = { id: uuidv4(), layer: page.objects.length, locked: false, visible: true, rotation: 0, ...action.payload };
          page.objects = [...page.objects, newObject];
          page.__newId = newObject.id;
          return page;
        }),
        selectedObjectIds: [action.payload.id || undefined].filter(Boolean),
        __selectNew: true,
      };
    case 'ADD_OBJECTS': {
      const ids = [];
      const next = withCurrentPage(state, (page) => {
        const added = action.payload.map((o, i) => {
          const obj = { id: uuidv4(), layer: page.objects.length + i, locked: false, visible: true, rotation: 0, ...o };
          ids.push(obj.id);
          return obj;
        });
        page.objects = [...page.objects, ...added];
        return page;
      });
      return { ...next, selectedObjectIds: ids };
    }
    case 'UPDATE_OBJECT':
      // a cropped picture scales with its frame (lib/pictureCrop.withCropScaled); crop mode passes keepCrop
      return withCurrentPage(state, (page) => {
        page.objects = page.objects.map((obj) => (obj.id === action.payload.id ? withCropScaled(obj, action.payload.data, action.payload.keepCrop) : obj));
        return page;
      });
    case 'UPDATE_OBJECT_PROPS':
      return withCurrentPage(state, (page) => {
        page.objects = page.objects.map((obj) =>
          action.payload.ids.includes(obj.id) ? { ...obj, properties: { ...(obj.properties || {}), ...action.payload.props } } : obj
        );
        return page;
      });
    case 'UPDATE_OBJECT_PROPS_BY_ID': {
      // payload: { ids, props } — merge `props` into the objects with these ids wherever they are (any page, scratch
      // area). Used when a linked story is edited from a continuation box: the story lives in its head, which may sit
      // on another page.
      const ids = new Set(action.payload.ids || []);
      return mapDocObjects(state, (o) => (ids.has(o.id) ? { ...o, properties: { ...(o.properties || {}), ...action.payload.props } } : o));
    }
    case 'UPDATE_OBJECT_PROPS_ANYWHERE': {
      // payload: [{ pageIndex, id, props }] — used by Replace All, which touches objects on every page.
      const byPage = new Map();
      action.payload.forEach((u) => { if (!byPage.has(u.pageIndex)) byPage.set(u.pageIndex, []); byPage.get(u.pageIndex).push(u); });
      const newPages = state.pages.map((pg, i) => {
        const ups = byPage.get(i);
        if (!ups) return pg;
        return { ...pg, objects: pg.objects.map((obj) => { const u = ups.find((x) => x.id === obj.id); return u ? { ...obj, properties: { ...(obj.properties || {}), ...u.props } } : obj; }) };
      });
      return { ...state, pages: newPages };
    }
    case 'UPDATE_OBJECTS':
      return withCurrentPage(state, (page) => {
        page.objects = page.objects.map((obj) => {
          const update = action.payload.find((u) => u.id === obj.id);
          return update ? withCropScaled(obj, update.data, update.keepCrop) : obj;
        });
        return page;
      });
    case 'DELETE_OBJECTS': {
      const ids = action.payload;
      // Stories close over the removed boxes (and keep their text when the first box goes) — wherever the rest of the
      // story lives, this page or another.
      const patches = state.viewMode === 'master' ? new Map() : removeFromStories(documentObjects(state.pages, state.scratch), ids);
      const repaired = patches.size ? patchDocProps(state, patches) : state;
      const next = withCurrentPage(repaired, (page) => {
        page.objects = page.objects.filter((obj) => !ids.includes(obj.id));
        return page;
      });
      return { ...next, selectedObjectIds: [], editingObjectId: null };
    }
    case 'REORDER_LAYERS':
      return withCurrentPage(state, (page) => {
        const { fromIndex, toIndex } = action.payload;
        const objects = [...page.objects];
        const [moved] = objects.splice(fromIndex, 1);
        objects.splice(toIndex, 0, moved);
        page.objects = objects.map((o, i) => ({ ...o, layer: i }));
        return page;
      });
    case 'BRING_FORWARD':
    case 'SEND_BACKWARD':
    case 'BRING_TO_FRONT':
    case 'SEND_TO_BACK': {
      if (state.selectedObjectIds.length < 1) return state;
      return withCurrentPage(state, (page) => {
        let objects = [...page.objects].sort((a, b) => a.layer - b.layer);
        const selected = objects.filter((o) => state.selectedObjectIds.includes(o.id));
        const rest = objects.filter((o) => !state.selectedObjectIds.includes(o.id));
        if (action.type === 'BRING_TO_FRONT') objects = [...rest, ...selected];
        else if (action.type === 'SEND_TO_BACK') objects = [...selected, ...rest];
        else {
          const dir = action.type === 'BRING_FORWARD' ? 1 : -1;
          const ids = state.selectedObjectIds;
          const order = objects.map((o) => o.id);
          const indices = ids.map((id) => order.indexOf(id)).sort((a, b) => (dir > 0 ? b - a : a - b));
          for (const idx of indices) {
            const t = idx + dir;
            if (t < 0 || t >= objects.length || ids.includes(objects[t].id)) continue;
            [objects[idx], objects[t]] = [objects[t], objects[idx]];
          }
        }
        page.objects = objects.map((o, i) => ({ ...o, layer: i }));
        return page;
      });
    }
    case 'GROUP_OBJECTS': {
      if (state.selectedObjectIds.length < 2) return state;
      const groupId = uuidv4();
      const next = withCurrentPage(state, (page) => {
        const selectedObjs = page.objects.filter((o) => state.selectedObjectIds.includes(o.id));
        const minX = Math.min(...selectedObjs.map((o) => o.x));
        const minY = Math.min(...selectedObjs.map((o) => o.y));
        const maxX = Math.max(...selectedObjs.map((o) => o.x + o.width));
        const maxY = Math.max(...selectedObjs.map((o) => o.y + o.height));
        const group = {
          id: groupId, type: 'group', x: minX, y: minY, width: maxX - minX, height: maxY - minY, rotation: 0,
          layer: page.objects.length, locked: false, visible: true, properties: {},
          children: selectedObjs.map((o, i) => ({ ...o, x: o.x - minX, y: o.y - minY, layer: i })),
        };
        page.objects = [...page.objects.filter((o) => !state.selectedObjectIds.includes(o.id)), group].map((o, i) => ({ ...o, layer: i }));
        return page;
      });
      return { ...next, selectedObjectIds: [groupId] };
    }
    case 'UNGROUP_OBJECTS': {
      if (state.selectedObjectIds.length !== 1) return state;
      let newIds = [];
      const next = withCurrentPage(state, (page) => {
        const groupObj = page.objects.find((o) => o.id === state.selectedObjectIds[0]);
        if (!groupObj || groupObj.type !== 'group') return page;
        const sx = groupObj.width / Math.max(1, groupObj.children.reduce((m, c) => Math.max(m, c.x + c.width), 0));
        const sy = groupObj.height / Math.max(1, groupObj.children.reduce((m, c) => Math.max(m, c.y + c.height), 0));
        const ungrouped = groupObj.children.map((child, i) => ({
          // a cropped picture inside a resized group keeps its picture scaled with the frame it gets back
          ...withCropScaled(child, { width: child.width * sx, height: child.height * sy }),
          id: uuidv4(),
          x: groupObj.x + child.x * sx,
          y: groupObj.y + child.y * sy,
          layer: page.objects.length + i,
        }));
        newIds = ungrouped.map((o) => o.id);
        page.objects = [...page.objects.filter((o) => o.id !== groupObj.id), ...ungrouped].map((o, i) => ({ ...o, layer: i }));
        return page;
      });
      return newIds.length ? { ...next, selectedObjectIds: newIds } : state;
    }
    case 'ALIGN_OBJECTS': {
      const { mode, relativeTo } = action.payload; // left|center|right|top|middle|bottom|distributeH|distributeV
      const objects = getActiveObjects(state).filter((o) => state.selectedObjectIds.includes(o.id));
      if (!objects.length) return state;
      const page = state.pages[state.currentPageIndex];
      const useCanvas = relativeTo === 'page' || objects.length === 1;
      const bounds = useCanvas
        ? { x: 0, y: 0, w: page.width, h: page.height }
        : {
            x: Math.min(...objects.map((o) => o.x)), y: Math.min(...objects.map((o) => o.y)),
            w: Math.max(...objects.map((o) => o.x + o.width)) - Math.min(...objects.map((o) => o.x)),
            h: Math.max(...objects.map((o) => o.y + o.height)) - Math.min(...objects.map((o) => o.y)),
          };
      const updates = [];
      if (mode === 'distributeH' || mode === 'distributeV') {
        const key = mode === 'distributeH' ? 'x' : 'y';
        const size = mode === 'distributeH' ? 'width' : 'height';
        const sorted = [...objects].sort((a, b) => a[key] - b[key]);
        const total = sorted.reduce((s, o) => s + o[size], 0);
        const span = (mode === 'distributeH' ? bounds.w : bounds.h) - total;
        const gap = span / Math.max(1, sorted.length - 1);
        let cursor = mode === 'distributeH' ? bounds.x : bounds.y;
        for (const o of sorted) {
          updates.push({ id: o.id, data: { [key]: cursor } });
          cursor += o[size] + gap;
        }
      } else {
        for (const o of objects) {
          const d = {};
          if (mode === 'left') d.x = bounds.x;
          if (mode === 'center') d.x = bounds.x + bounds.w / 2 - o.width / 2;
          if (mode === 'right') d.x = bounds.x + bounds.w - o.width;
          if (mode === 'top') d.y = bounds.y;
          if (mode === 'middle') d.y = bounds.y + bounds.h / 2 - o.height / 2;
          if (mode === 'bottom') d.y = bounds.y + bounds.h - o.height;
          updates.push({ id: o.id, data: d });
        }
      }
      return withCurrentPage(state, (pg) => {
        pg.objects = pg.objects.map((obj) => {
          const u = updates.find((x) => x.id === obj.id);
          return u ? { ...obj, ...u.data } : obj;
        });
        return pg;
      });
    }
    case 'LINK_TEXTBOXES': {
      // Publisher: a story continues into an *empty*, unlinked text box — on this page or any other.
      const { fromId, toId } = action.payload;
      if (!fromId || !toId || fromId === toId) return state;
      const all = documentObjects(state.pages, state.scratch);
      const from = all.find((o) => o.id === fromId), to = all.find((o) => o.id === toId);
      if (!isTextType(from) || !isTextType(to) || from.properties?.linkNextId || to.properties?.linkPrevId) return state;
      if (storyPlainText(to.properties || {}).trim()) return state;
      if (storyChainOf(fromId, all).some((o) => o.id === toId)) return state; // would close a loop
      return mapDocObjects(state, (obj) => {
        if (obj.id === fromId) return { ...obj, properties: { ...obj.properties, linkNextId: toId } };
        if (obj.id === toId) return { ...obj, properties: { ...obj.properties, linkPrevId: fromId, text: '', html: undefined } };
        return obj;
      });
    }
    case 'UNLINK_TEXTBOX': {
      const id = action.payload;
      const obj = documentObjects(state.pages, state.scratch).find((o) => o.id === id);
      if (!obj) return state;
      const nextId = obj.properties?.linkNextId;
      if (!nextId) return state;
      const overflow = state.textFlow[id] || '';
      // the cut-off box keeps the words that flowed into it — formatted when the story was (flowHtmlKey)
      const overflowHtml = stripFlowMarkersHtml(richFlowFor(flowHtmlOf(state.textFlow, id), overflow)); // a box of its own: a real paragraph start again
      return mapDocObjects(state, (o) => {
        if (o.id === id) return { ...o, properties: { ...o.properties, linkNextId: null } };
        if (o.id === nextId) return { ...o, properties: { ...o.properties, linkPrevId: null, text: overflow, html: overflowHtml || undefined } };
        return o;
      });
    }
    case 'AUTOFLOW_STORY': {
      // payload from lib/storyFlow.planAutoflow: { chain: [tailId, ...existing empty boxes, ...new boxes],
      //   additions: [{ insertAt, page }], flows: { id: overflow } }. One history step.
      const { chain = [], additions = [], flows = {} } = action.payload || {};
      if (chain.length < 2 || state.viewMode === 'master') return state;
      const pages = [...state.pages];
      additions.forEach(({ insertAt, page }) => pages.splice(Math.min(Math.max(0, insertAt), pages.length), 0, page));
      const pos = new Map(chain.map((id, i) => [id, i]));
      const linked = mapDocObjects({ ...state, pages }, (o) => {
        const i = pos.get(o.id);
        if (i === undefined) return o;
        const p = { ...(o.properties || {}), linkNextId: chain[i + 1] || null };
        if (i > 0) { p.linkPrevId = chain[i - 1]; p.text = ''; p.html = undefined; } // continuation boxes show the flow, not their own text
        return { ...o, properties: p };
      });
      return { ...linked, textFlow: { ...state.textFlow, ...flows } };
    }
    case 'SELECT_OBJECT_ANYWHERE': {
      // Select an object wherever it is: switches to its page first (story navigation across pages).
      const id = action.payload;
      if (!id || state.viewMode === 'master') return state;
      const idx = state.pages.findIndex((pg) => (pg.objects || []).some((o) => o.id === id));
      const inScratch = scratchObjectsOf(state).some((o) => o.id === id);
      if (idx < 0 && !inScratch) return state;
      const base = idx >= 0 && idx !== state.currentPageIndex ? { ...state, currentPageIndex: idx } : state;
      return { ...base, selectedObjectIds: [id], editingObjectId: null };
    }
    case 'SET_TEXT_FLOW': {
      // { id, overflow, overflowHtml? } — the plain overflow of a box and, for formatted stories, the same overflow as
      // HTML under flowHtmlKey(id) (lib/storyEdit.js) so the next box shows it with its formatting
      const { id, overflow, overflowHtml } = action.payload;
      const hk = flowHtmlKey(id);
      if ((state.textFlow[id] || '') === (overflow || '') && (state.textFlow[hk] || '') === (overflowHtml || '')) return state;
      const textFlow = { ...state.textFlow, [id]: overflow || '' };
      if (overflowHtml) textFlow[hk] = overflowHtml; else delete textFlow[hk];
      return { ...state, textFlow };
    }
    case 'SET_TEXT_FLOWS': {
      // { id: overflow } for boxes on other pages, measured off-screen (hooks/useStoryFlow)
      const entries = Object.entries(action.payload || {}).filter(([id, ov]) => (state.textFlow[id] || '') !== (ov || ''));
      if (!entries.length) return state;
      const textFlow = { ...state.textFlow };
      entries.forEach(([id, ov]) => { textFlow[id] = ov || ''; });
      return { ...state, textFlow };
    }

    // ---------- grid & guides ----------
    case 'SET_GRID_VISIBLE':
      return { ...state, gridVisible: action.payload };
    case 'SET_GRID_SPACING':
      return { ...state, gridSpacing: action.payload };
    case 'SET_GRID_PRINTABLE':
      return { ...state, gridPrintable: action.payload };
    case 'ADD_GUIDE':
      return { ...state, guides: [...state.guides, action.payload] };
    case 'UPDATE_GUIDE':
      return { ...state, guides: state.guides.map((g) => (g.id === action.payload.id ? { ...g, ...action.payload.data } : g)) };
    case 'DELETE_GUIDE':
      return { ...state, guides: state.guides.filter((g) => g.id !== action.payload) };
    case 'CLEAR_GUIDES':
      return { ...state, guides: [] };
    case 'SET_SNAP_TO_GUIDES':
      return { ...state, snapToGuides: action.payload };
    case 'SET_SNAP_TO_GRID':
      return { ...state, snapToGrid: action.payload };
    case 'SET_SHOW_ALIGNMENT_GUIDES':
      return { ...state, showAlignmentGuides: action.payload };
    case 'SET_TEXT_SETTINGS':
      return { ...state, textSettings: { ...state.textSettings, ...action.payload } };
    case 'SET_SHAPE_SETTINGS':
      return { ...state, shapeSettings: { ...state.shapeSettings, ...action.payload } };

    // ---------- panels ----------
    case 'SET_LEFT_PANEL_TAB':
      return { ...state, leftPanelTab: action.payload };
    case 'SET_RIGHT_PANEL_TAB':
      return { ...state, rightPanelTab: action.payload };
    case 'SHOW_PHOTO_EDITOR':
      return { ...state, showPhotoEditor: true, photoEditorImage: action.payload };
    case 'HIDE_PHOTO_EDITOR':
      return { ...state, showPhotoEditor: false, photoEditorImage: null };
    case 'SHOW_IMAGE_GALLERY':
      return { ...state, showImageGallery: true, galleryInsertMode: action.payload?.mode || 'insert' };
    case 'HIDE_IMAGE_GALLERY':
      return { ...state, showImageGallery: false, galleryInsertMode: 'insert' };

    // ---------- clipboard ----------
    case 'COPY_OBJECTS': {
      const objs = getActiveObjects(state).filter((o) => state.selectedObjectIds.includes(o.id));
      return { ...state, clipboard: objs };
    }
    case 'CUT_OBJECTS': {
      const objs = getActiveObjects(state).filter((o) => state.selectedObjectIds.includes(o.id));
      const next = withCurrentPage(state, (page) => {
        page.objects = page.objects.filter((o) => !state.selectedObjectIds.includes(o.id));
        return page;
      });
      return { ...next, clipboard: objs, selectedObjectIds: [], editingObjectId: null };
    }
    case 'PASTE_OBJECTS': {
      if (state.clipboard.length === 0) return state;
      const ids = [];
      const next = withCurrentPage(state, (page) => {
        // links between copied objects survive; the group is nudged and kept inside this page (it may come from another publication)
        const pasted = fitGroupIntoPage(cloneObjectsKeepingLinks(state.clipboard).map((o, i) => ({ ...o, x: o.x + 20, y: o.y + 20, layer: page.objects.length + i })), page);
        pasted.forEach((o) => ids.push(o.id));
        page.objects = [...page.objects, ...pasted];
        return page;
      });
      return { ...next, selectedObjectIds: ids };
    }
    // ---------- page clipboard (works across document tabs) ----------
    case 'COPY_PAGE': {
      const page = state.pages[action.payload?.index ?? state.currentPageIndex];
      if (!page) return state;
      return { ...state, pageClipboard: { page: JSON.parse(JSON.stringify(page)), sourceName: state.projectName, copiedAt: Date.now() } };
    }
    case 'PASTE_PAGE': {
      if (!state.pageClipboard?.page || state.viewMode === 'master') return state;
      const at = Math.min(state.pages.length, (action.payload?.after ?? state.currentPageIndex) + 1);
      const src = state.pageClipboard.page;
      const page = clonePageKeepingLinks(src, pastedPageName(src.name, state.pages));
      // the source master does not exist here → use the master of the page it is pasted after
      const anchor = state.pages[Math.max(0, at - 1)];
      page.masterId = src.masterId === 'none' ? 'none' : (state.masters[src.masterId] && !action.payload?.fromOtherPublication ? src.masterId : (anchor?.masterId || firstMasterId(state.masters)));
      const pages = [...state.pages];
      pages.splice(at, 0, page);
      return { ...state, pages, currentPageIndex: at, selectedObjectIds: [], editingObjectId: null };
    }
    // ---------- copy straight into another open publication (its tab keeps a snapshot; we edit that) ----------
    case 'COPY_OBJECTS_TO_TAB': {
      const { tabId, ids: wanted } = action.payload || {};
      const objs = getActiveObjects(state).filter((o) => (wanted || state.selectedObjectIds).includes(o.id));
      if (!objs.length) return state;
      if (tabId === state.activeTabId) return editorReducer({ ...state, clipboard: objs }, { type: 'PASTE_OBJECTS' });
      const tab = state.docTabs.find((t) => t.id === tabId);
      if (!tab?.doc) return state;
      const doc = tab.doc;
      const idx = Math.min(doc.currentPageIndex || 0, doc.pages.length - 1);
      const target = doc.pages[idx];
      const pasted = fitGroupIntoPage(cloneObjectsKeepingLinks(objs).map((o, i) => ({ ...o, layer: target.objects.length + i })), target);
      const pages = doc.pages.map((pg, i) => (i === idx ? { ...pg, objects: [...pg.objects, ...pasted] } : pg));
      const nextDoc = changeDocWithHistory(doc, (d) => ({ ...d, pages, selectedObjectIds: pasted.map((o) => o.id) }));
      return { ...state, docTabs: state.docTabs.map((t) => (t.id === tabId ? { ...t, doc: nextDoc } : t)) };
    }
    case 'COPY_PAGE_TO_TAB': {
      const { tabId, index } = action.payload || {};
      const src = state.pages[index ?? state.currentPageIndex];
      if (!src) return state;
      if (tabId === state.activeTabId) return editorReducer({ ...state, pageClipboard: { page: src, sourceName: state.projectName } }, { type: 'PASTE_PAGE' });
      const tab = state.docTabs.find((t) => t.id === tabId);
      if (!tab?.doc) return state;
      const doc = tab.doc;
      const at = Math.min(doc.pages.length, (doc.currentPageIndex || 0) + 1);
      const page = clonePageKeepingLinks(src, pastedPageName(src.name, doc.pages));
      const anchor = doc.pages[Math.max(0, at - 1)];
      page.masterId = src.masterId === 'none' ? 'none' : (anchor?.masterId || firstMasterId(doc.masters || {}));
      const pages = [...doc.pages];
      pages.splice(at, 0, page);
      const nextDoc = changeDocWithHistory(doc, (d) => ({ ...d, pages, currentPageIndex: at, selectedObjectIds: [] }));
      return { ...state, docTabs: state.docTabs.map((t) => (t.id === tabId ? { ...t, doc: nextDoc } : t)) };
    }
    // ---------- cloud autosave ----------
    case 'MARK_TAB_SAVED': {
      // a background tab's snapshot was autosaved: { tabId, projectId, revision, savedAt }
      const { tabId, projectId, revision, savedAt } = action.payload || {};
      // the user switched to that tab while the request was in flight → it is the live document now
      if (tabId === state.activeTabId) return (!projectId || state.projectId === projectId) ? editorReducer(state, { type: 'MARK_SAVED', payload: { at: savedAt, revision } }) : state;
      return { ...state, docTabs: state.docTabs.map((t) => (t.id === tabId && t.doc && (!projectId || t.doc.projectId === projectId) ? { ...t, doc: { ...t.doc, savedRevision: Math.max(Math.min(revision ?? t.doc.docRevision, t.doc.docRevision), t.doc.savedRevision ?? -1), lastSavedAt: savedAt || new Date().toISOString() } } : t)) };
    }
    case 'SET_AUTOSAVE': {
      const on = !!action.payload;
      writePref('wp.autosave', on);
      return { ...state, autosave: on };
    }
    case 'SET_AUTOSAVE_STATUS':
      return { ...state, autosaveStatus: action.payload };
    case 'SET_FONT_PREVIEW': {
      // { ids: [...], family } | null — the font picker's hover preview; never touches the document
      const pl = action.payload && action.payload.family && action.payload.ids?.length ? { ids: action.payload.ids, family: action.payload.family } : null;
      if (!pl && !state.fontPreview) return state;
      if (pl && state.fontPreview && state.fontPreview.family === pl.family && state.fontPreview.ids.join() === pl.ids.join()) return state;
      return { ...state, fontPreview: pl };
    }
    case 'DUPLICATE_OBJECTS': {
      const objs = getActiveObjects(state).filter((o) => state.selectedObjectIds.includes(o.id));
      if (!objs.length) return state;
      const ids = [];
      const next = withCurrentPage(state, (page) => {
        const dup = objs.map((obj, i) => {
          const o = { ...regenerateIds(obj), x: obj.x + 20, y: obj.y + 20, layer: page.objects.length + i };
          ids.push(o.id);
          return o;
        });
        page.objects = [...page.objects, ...dup];
        return page;
      });
      return { ...next, selectedObjectIds: ids };
    }

    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    case 'SET_SAVING':
      return { ...state, isSaving: action.payload };

    // ---------- history ----------
    case 'PUSH_HISTORY': {
      const pagesJson = JSON.stringify(state.pages), mastersJson = JSON.stringify(state.masters), scratchJson = JSON.stringify(state.scratch || { objects: [] });
      const newHistory = state.history.slice(0, state.historyIndex + 1);
      const top = newHistory[newHistory.length - 1];
      // Identical to the current top snapshot → nothing to record (avoids empty Undo steps).
      if (top && JSON.stringify(top.pages) === pagesJson && JSON.stringify(top.masters || state.masters) === mastersJson && JSON.stringify(top.scratch || state.scratch || { objects: [] }) === scratchJson) {
        return newHistory.length === state.history.length && !state.historyDirty ? state : { ...state, history: newHistory, historyIndex: newHistory.length - 1, historyDirty: false };
      }
      // customSchemes rides along (immutable list) so undoing a scheme edit / "Create & Recolour" restores the palette with the objects
      const entry = { pages: JSON.parse(pagesJson), masters: JSON.parse(mastersJson), scratch: JSON.parse(scratchJson), selectedObjectIds: [...state.selectedObjectIds], customSchemes: state.customSchemes };
      newHistory.push(entry);
      if (newHistory.length > (state.maxHistorySize || 50)) newHistory.shift();
      return { ...state, history: newHistory, historyIndex: newHistory.length - 1, historyDirty: false };
    }
    case 'UNDO': {
      if (state.historyIndex < 0 || !state.history.length) return state;
      // History entries are snapshots taken *before* each recorded action (plus a debounced snapshot after
      // continuous edits such as typing or dragging). When the latest action was discrete (e.g. Delete, Replace All)
      // the current document is therefore not yet in the history: record it first so Redo can come back, then step
      // to the snapshot that preceded it. Without this, two discrete actions in a row were undone by a single Undo.
      const top = state.history[state.historyIndex];
      const atTop = state.historyIndex === state.history.length - 1;
      const dirty = atTop && top && (JSON.stringify(top.pages) !== JSON.stringify(state.pages) || JSON.stringify(top.masters || state.masters) !== JSON.stringify(state.masters) || JSON.stringify(top.scratch || state.scratch || { objects: [] }) !== JSON.stringify(state.scratch || { objects: [] }));
      if (dirty) {
        const entry = { pages: JSON.parse(JSON.stringify(state.pages)), masters: JSON.parse(JSON.stringify(state.masters)), scratch: JSON.parse(JSON.stringify(state.scratch || { objects: [] })), selectedObjectIds: [...state.selectedObjectIds], customSchemes: state.customSchemes };
        const history = [...state.history, entry];
        const restored = { ...state, pages: JSON.parse(JSON.stringify(top.pages)), masters: JSON.parse(JSON.stringify(top.masters || state.masters)), scratch: JSON.parse(JSON.stringify(top.scratch || state.scratch || { objects: [] })), ...restoreSchemes(state, top), selectedObjectIds: [], editingObjectId: null, history, historyIndex: state.historyIndex };
        return keepWrapEditSelection(state, selectionAfterHistory(state, restored, top.selectedObjectIds));
      }
      if (state.historyIndex <= 0) return state;
      const idx = state.historyIndex - 1;
      const prev = state.history[idx];
      if (!prev) return state;
      return keepWrapEditSelection(state, selectionAfterHistory(state, { ...state, pages: JSON.parse(JSON.stringify(prev.pages)), masters: JSON.parse(JSON.stringify(prev.masters || state.masters)), scratch: JSON.parse(JSON.stringify(prev.scratch || state.scratch || { objects: [] })), ...restoreSchemes(state, prev), selectedObjectIds: [], editingObjectId: null, historyIndex: idx }, prev.selectedObjectIds));
    }
    case 'REDO': {
      if (state.historyIndex >= state.history.length - 1) return state;
      const idx = state.historyIndex + 1;
      const next = state.history[idx];
      if (!next) return state;
      return keepWrapEditSelection(state, selectionAfterHistory(state, { ...state, pages: JSON.parse(JSON.stringify(next.pages)), masters: JSON.parse(JSON.stringify(next.masters || state.masters)), scratch: JSON.parse(JSON.stringify(next.scratch || state.scratch || { objects: [] })), ...restoreSchemes(state, next), selectedObjectIds: [], editingObjectId: null, historyIndex: idx }, next.selectedObjectIds));
    }
    case 'CLEAR_HISTORY':
      return { ...state, history: [], historyIndex: -1, historyDirty: false };

    default:
      return state;
  }
};

// ADD_OBJECT needs the generated id for selection; wrap reducer to handle that.
const CONTEXT_TAB_BY_TYPE = { textbox: 'text-box', text: 'text-box', shape: 'shape-format', image: 'picture-format', table: 'table-design' };

/** Marks the document dirty whenever a reducer produced a different document (pages, masters, guides, scheme, name). */
const withRevision = (state, next, action) => {
  if (next === state || ['SET_PROJECT', 'MARK_SAVED', 'MARK_TAB_SAVED', 'SWITCH_DOC_TAB', 'CLOSE_DOC_TAB', 'MOVE_DOC_TAB', 'RESTORE_SESSION', 'COPY_PAGE', 'SET_AUTOSAVE', 'SET_AUTOSAVE_STATUS'].includes(action.type)) return next;
  if (next.pages !== state.pages || next.masters !== state.masters || next.scratch !== state.scratch || next.layoutMode !== state.layoutMode || next.spreadStyle !== state.spreadStyle || next.guides !== state.guides || next.schemeId !== state.schemeId || next.customSchemes !== state.customSchemes || next.fontScheme !== state.fontScheme || next.projectName !== state.projectName || next.canvasUnit !== state.canvasUnit) {
    // historyDirty: the document differs from the top history snapshot (a discrete action right after a load, a drag in
    // progress…). Undo can already step back in that state (the reducer records the dirty document first); the flag keeps
    // the Undo button enabled to match.
    const historyDirty = ['UNDO', 'REDO', 'PUSH_HISTORY', 'CLEAR_HISTORY'].includes(action.type) ? false : (next.pages !== state.pages || next.masters !== state.masters || next.scratch !== state.scratch) ? true : next.historyDirty;
    return { ...next, docRevision: (state.docRevision || 0) + 1, historyDirty };
  }
  return next;
};

const rootReducer = (state, action) => withFontPreviewInvariant(withCropEditInvariant(withPointEditInvariant(withWrapEditInvariant(withRevision(state, coreReducer(state, action), action)))));

/** The font picker's hover preview only ever paints selected objects: a selection change (or Undo, delete…) drops it. */
const withFontPreviewInvariant = (next) => {
  if (!next.fontPreview) return next;
  const ok = next.fontPreview.ids.every((id) => next.selectedObjectIds.includes(id));
  return ok ? next : { ...next, fontPreview: null };
};

/** Crop mode lives while its picture is the single selected, unlocked picture with a source on the active page. */
const withCropEditInvariant = (next) => {
  if (!next.cropEditId) return next;
  const o = next.selectedObjectIds.length === 1 && next.selectedObjectIds[0] === next.cropEditId ? getActiveObjects(next).find((x) => x.id === next.cropEditId) : null;
  const ok = !!o && !o.locked && next.activeTool === 'select' && o.type === 'image' && !!o.properties?.src && !next.editingObjectId;
  return ok ? next : { ...next, cropEditId: null, cropAspect: null };
};

/**
 * "Edit Wrap Points" mode only makes sense while its object is the single selected object on the active page and
 * still carries custom points (Undo, Delete, page change, tool change, Reset… all leave the mode automatically).
 */
const withWrapEditInvariant = (next) => {
  if (!next.wrapEditId) return next;
  const o = next.selectedObjectIds.length === 1 && next.selectedObjectIds[0] === next.wrapEditId ? getActiveObjects(next).find((x) => x.id === next.wrapEditId) : null;
  const pts = o?.properties?.wrapPoints;
  const ok = !!o && !o.locked && next.activeTool === 'select' && Array.isArray(pts) && pts.length >= 3;
  return ok ? next : { ...next, wrapEditId: null };
};

/** Same rules for "Edit Points": the object must stay the single selected, unlocked freeform shape. */
const withPointEditInvariant = (next) => {
  if (!next.pointEditId) return next;
  const o = next.selectedObjectIds.length === 1 && next.selectedObjectIds[0] === next.pointEditId ? getActiveObjects(next).find((x) => x.id === next.pointEditId) : null;
  const ok = !!o && !o.locked && next.activeTool === 'select' && o.type === 'shape' && o.shapeType === 'freeform' && Array.isArray(o.properties?.freeform?.paths) && o.properties.freeform.paths.length > 0;
  return ok ? next : { ...next, pointEditId: null };
};

const coreReducer = (state, action) => {
  /* ---- document tabs: loading a publication opens it in its own tab (the one in progress stays open) ---- */
  if (action.type === 'SET_PROJECT' && action.tab !== 'replace') {
    const p = action.payload || {};
    if (p.id) {
      const other = state.docTabs.find((t) => t.id !== state.activeTabId && t.doc?.projectId === p.id);
      if (other) return coreReducer(state, { type: 'SWITCH_DOC_TAB', payload: { id: other.id } }); // already open → go there
    }
    if (isPristineDoc(state) || (p.id && p.id === state.projectId)) return editorReducer(state, action); // reuse the blank tab / reload in place
    const tabs = stashActiveDoc(state);
    const id = newTabId();
    const next = editorReducer(state, action);
    return { ...next, docTabs: [...tabs, { id, doc: null }], activeTabId: id };
  }
  if (action.type === 'SWITCH_DOC_TAB') {
    const id = action.payload?.id;
    const target = state.docTabs.find((t) => t.id === id);
    if (!target || id === state.activeTabId || !target.doc) return state;
    const tabs = stashActiveDoc(state).map((t) => (t.id === id ? { ...t, doc: null } : t));
    return restoreDoc({ ...state, docTabs: tabs, activeTabId: id }, target.doc);
  }
  if (action.type === 'CLOSE_DOC_TAB') {
    const id = action.payload?.id || state.activeTabId;
    const idx = state.docTabs.findIndex((t) => t.id === id);
    if (idx < 0) return state;
    const tabs = state.docTabs.filter((t) => t.id !== id);
    if (id !== state.activeTabId) return { ...state, docTabs: tabs };
    if (!tabs.length) {
      // last publication closed → a fresh blank one and the File ▸ Home screen (the "nothing open" state)
      const fresh = editorReducer(state, { type: 'SET_PROJECT', payload: blankProjectPayload() });
      const nid = newTabId();
      return { ...fresh, docTabs: [{ id: nid, doc: null }], activeTabId: nid, fileOpen: true, backstageSection: 'home' };
    }
    const neighbour = tabs[Math.min(idx, tabs.length - 1)];
    return restoreDoc({ ...state, docTabs: tabs.map((t) => (t.id === neighbour.id ? { ...t, doc: null } : t)), activeTabId: neighbour.id }, neighbour.doc || pickDoc(initialState));
  }
  if (action.type === 'MOVE_DOC_TAB') {
    const { id, to } = action.payload || {};
    const from = state.docTabs.findIndex((t) => t.id === id);
    if (from < 0 || to < 0 || to >= state.docTabs.length || from === to) return state;
    const tabs = [...state.docTabs];
    const [tab] = tabs.splice(from, 1);
    tabs.splice(to, 0, tab);
    return { ...state, docTabs: tabs };
  }
  if (action.type === 'RESTORE_SESSION') {
    // { tabs: [{ id, doc }], activeTabId } from the last browser session (lib/sessionStore) — replaces a pristine start
    const tabs = (action.payload?.tabs || []).filter((t) => t && t.id && t.doc && Array.isArray(t.doc.pages) && t.doc.pages.length);
    if (!tabs.length) return state;
    const freshDoc = (d) => ({ ...d, history: [], historyIndex: -1, historyDirty: false });
    if (!(isPristineDoc(state) && state.docTabs.length === 1)) {
      // something is already open (e.g. a share link loaded first) → add the session's publications as background tabs
      const ids = new Set(state.docTabs.map((t) => t.id));
      const projectIds = new Set([state.projectId, ...state.docTabs.map((t) => t.doc?.projectId)].filter(Boolean));
      const add = tabs.filter((t) => !ids.has(t.id) && !(t.doc.projectId && projectIds.has(t.doc.projectId))).map((t) => ({ id: t.id, doc: freshDoc(t.doc) }));
      return add.length ? { ...state, docTabs: [...state.docTabs, ...add] } : state;
    }
    const activeId = tabs.some((t) => t.id === action.payload.activeTabId) ? action.payload.activeTabId : tabs[0].id;
    const active = tabs.find((t) => t.id === activeId);
    const docTabs = tabs.map((t) => (t.id === activeId ? { id: t.id, doc: null } : { id: t.id, doc: freshDoc(t.doc) }));
    return { ...restoreDoc({ ...state, docTabs, activeTabId: activeId }, freshDoc(active.doc)), fileOpen: state.fileOpen };
  }
  if (action.type === 'ADD_OBJECT') {
    const id = action.payload.id || uuidv4();
    const next = editorReducer(state, { ...action, payload: { ...action.payload, id } });
    const { __selectNew, ...rest } = next;
    const ctxTab = CONTEXT_TAB_BY_TYPE[action.payload.type];
    return { ...rest, selectedObjectIds: [id], editingObjectId: null, ribbonTab: ctxTab || rest.ribbonTab, fileOpen: false };
  }
  if (action.type === 'SET_EDITING' && action.payload) {
    const next = editorReducer(state, action);
    const obj = getActiveObjects(next).find((o) => o.id === action.payload);
    // Editing text inside a shape shows the Text Box tools (Publisher: Text Box Tools ▸ Format appears next to Drawing Tools)
    const tab = obj ? (obj.type === 'shape' ? 'text-box' : CONTEXT_TAB_BY_TYPE[obj.type]) : null;
    return { ...next, ribbonTab: tab || next.ribbonTab };
  }
  return editorReducer(state, action);
};

const HISTORY_ACTIONS = new Set([
  'ADD_OBJECT', 'ADD_OBJECTS', 'UPDATE_OBJECT', 'UPDATE_OBJECT_PROPS', 'UPDATE_OBJECT_PROPS_ANYWHERE', 'UPDATE_OBJECT_PROPS_BY_ID', 'UPDATE_OBJECTS', 'DELETE_OBJECTS', 'PASTE_OBJECTS', 'CUT_OBJECTS', 'DUPLICATE_OBJECTS', 'PASTE_PAGE', 'COPY_OBJECTS_TO_TAB', 'COPY_PAGE_TO_TAB',
  'REORDER_LAYERS', 'BRING_FORWARD', 'SEND_BACKWARD', 'BRING_TO_FRONT', 'SEND_TO_BACK', 'GROUP_OBJECTS', 'UNGROUP_OBJECTS', 'ALIGN_OBJECTS',
  'UPDATE_PAGE', 'ADD_PAGE', 'DELETE_PAGE', 'MOVE_PAGE', 'LINK_TEXTBOXES', 'UNLINK_TEXTBOX', 'AUTOFLOW_STORY', 'UPDATE_MASTER', 'UPDATE_ALL_PAGES_SIZE', 'REPLACE_DOCUMENT_CONTENT',
]);
const CONTINUOUS_ACTIONS = new Set(['UPDATE_OBJECT', 'UPDATE_OBJECT_PROPS', 'UPDATE_OBJECT_PROPS_BY_ID', 'UPDATE_OBJECTS', 'UPDATE_PAGE']);

export const EditorProvider = ({ children }) => {
  const [state, dispatch] = useReducer(rootReducer, initialState);
  const stateRef = useRef(state);
  stateRef.current = state;
  // Baseline grid (Layout Guides ▸ Baseline Guides) → layout-engine store, synced during render so every text box,
  // thumbnail and export rendered in this pass (all descendants) lays out against the current grid.
  setBaselineGrid(getBaselineGuide(state.guides, state.viewOptions?.baselineSpacing));
  const historyTimerRef = useRef(null);
  const lastContinuousRef = useRef(0);

  const historyDispatch = useCallback((action) => {
    if (['UNDO', 'REDO', 'PUSH_HISTORY', 'CLEAR_HISTORY'].includes(action.type)) {
      if ((action.type === 'UNDO' || action.type === 'REDO') && historyTimerRef.current) { clearTimeout(historyTimerRef.current); historyTimerRef.current = null; }
      dispatch(action);
      return;
    }
    if (HISTORY_ACTIONS.has(action.type)) {
      const now = Date.now();
      if (CONTINUOUS_ACTIONS.has(action.type)) {
        // A burst of continuous edits (typing, dragging, a slider) is one Undo step: the snapshot is taken when the burst
        // starts (no continuous edit for half a second) and the end state is recorded half a second after it stops.
        // Recording every 500 ms *during* the burst (the former rule) made Undo peel typed text off a few letters at a time.
        if (now - lastContinuousRef.current >= 500) {
          dispatch({ type: 'PUSH_HISTORY' }); // a no-op when the top snapshot already equals the document
        }
        lastContinuousRef.current = now;
        if (historyTimerRef.current) clearTimeout(historyTimerRef.current);
        historyTimerRef.current = setTimeout(() => {
          historyTimerRef.current = null;
          dispatch({ type: 'PUSH_HISTORY' });
        }, 500);
      } else {
        dispatch({ type: 'PUSH_HISTORY' });
      }
    }
    dispatch(action);
  }, []);

  const clearPendingHistory = useCallback(() => {
    // A debounced snapshot still pending after Undo / Redo would re-record the *undone* state on top of the history and
    // cut off the Redo entry — drop it (Undo already records a dirty document before stepping back).
    if (historyTimerRef.current) { clearTimeout(historyTimerRef.current); historyTimerRef.current = null; }
  }, []);
  const undo = useCallback(() => { clearPendingHistory(); dispatch({ type: 'UNDO' }); }, [clearPendingHistory]);
  const redo = useCallback(() => { clearPendingHistory(); dispatch({ type: 'REDO' }); }, [clearPendingHistory]);
  const canUndo = state.historyIndex > 0 || (state.historyIndex === 0 && !!state.historyDirty);
  const canRedo = state.historyIndex < state.history.length - 1;

  useEffect(() => {
    const handleKeyDown = (e) => {
      const t = e.target;
      if (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable) return;
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z' && !e.shiftKey) { e.preventDefault(); undo(); }
      if ((e.ctrlKey || e.metaKey) && (e.key.toLowerCase() === 'y' || (e.key.toLowerCase() === 'z' && e.shiftKey))) { e.preventDefault(); redo(); }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [undo, redo]);

  const historyEmpty = state.history.length === 0;
  useEffect(() => {
    if (historyEmpty) dispatch({ type: 'PUSH_HISTORY' });
  }, [historyEmpty]);

  /* ---- open-publications session: restore on start, persist (debounced) on every document / tab change ---- */
  const sessionReady = useRef(false);
  const sessionTimer = useRef(null);
  const persistSession = () => {
    const s = stateRef.current;
    const tabs = s.docTabs.map((t) => ({ id: t.id, doc: docForSession(t.id === s.activeTabId ? pickDoc(s) : t.doc) })).filter((t) => t.doc);
    saveSession({ activeTabId: s.activeTabId, tabs });
  };
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const session = await loadSession();
      if (cancelled) return;
      const tabs = (session?.tabs || []).filter((t) => t?.doc && !isPristineDoc(t.doc));
      // a pristine start is replaced by the session; anything already open (a share link) keeps the front and the
      // session's publications join as background tabs
      if (tabs.length) {
        dispatch({ type: 'RESTORE_SESSION', payload: { tabs, activeTabId: session.activeTabId } });
        const dirty = tabs.filter((t) => t.doc.docRevision !== t.doc.savedRevision).length;
        toast.success(`Restored ${tabs.length} open publication${tabs.length === 1 ? '' : 's'} from your last session`, { id: 'session-restore', description: dirty ? `${dirty} with unsaved changes — save them when you are ready.` : 'Publications stay open until you close their tab.', duration: 6000 });
      }
      sessionReady.current = true;
      // a publication opened by a link (/app?project=…) may have arrived before the session was read — the change
      // effect below skipped it, so persist what is open now (otherwise a refresh before the next change loses the tab)
      if (sessionTimer.current) clearTimeout(sessionTimer.current);
      sessionTimer.current = setTimeout(persistSession, 1200);
    })();
    return () => { cancelled = true; };
  }, []);
  useEffect(() => {
    if (!sessionReady.current) return;
    if (sessionTimer.current) clearTimeout(sessionTimer.current);
    sessionTimer.current = setTimeout(persistSession, 1200);
    return () => { if (sessionTimer.current) clearTimeout(sessionTimer.current); };
  }, [state.docTabs, state.activeTabId, state.docRevision, state.savedRevision, state.projectName, state.projectId, state.currentPageIndex, state.zoom]);
  useEffect(() => {
    // flush right away when the page is being left (reload / tab close) instead of waiting for the debounce
    const flush = () => {
      if (!sessionReady.current) return;
      if (sessionTimer.current) { clearTimeout(sessionTimer.current); sessionTimer.current = null; }
      persistSession();
    };
    window.addEventListener('pagehide', flush);
    return () => window.removeEventListener('pagehide', flush);
  }, []);

  const getCurrentPage = useCallback(() => state.pages[state.currentPageIndex], [state.pages, state.currentPageIndex]);
  // Normal view: the current page's master (first master for pages that ignore theirs — callers check page.masterId);
  // Master Page view: the master being edited.
  const getActiveMaster = useCallback(() => activeMasterOf(state), [state]);
  const getActiveObjectsList = useCallback(() => getActiveObjects(state), [state]);
  const getSelectedObjects = useCallback(() => getActiveObjects(state).filter((obj) => state.selectedObjectIds.includes(obj.id)), [state]);
  const getObjectById = useCallback((id) => getActiveObjects(state).find((obj) => obj.id === id), [state]);

  const pxToUnit = useCallback((px, unit = state.canvasUnit, digits = 2) => {
    const DPI = 96;
    switch (unit) {
      case 'cm': return ((px / DPI) * 2.54).toFixed(digits);
      case 'in': return (px / DPI).toFixed(digits);
      case 'pt': return ((px * 72) / DPI).toFixed(Math.min(digits, 1)); // points read best to a tenth (612.0 × 792.0 pt)
      default: return Math.round(px);
    }
  }, [state.canvasUnit]);
  const unitToPx = useCallback((value, unit = state.canvasUnit) => {
    const DPI = 96;
    const v = parseFloat(value) || 0;
    switch (unit) {
      case 'cm': return (v / 2.54) * DPI;
      case 'in': return v * DPI;
      case 'pt': return (v * DPI) / 72;
      default: return v;
    }
  }, [state.canvasUnit]);

  // Only an explicit switch is remembered (not the value read on mount), so "follow the system" stays in effect until the
  // user picks a theme here or on the landing page. Compared against the last applied value (robust to StrictMode re-runs).
  const appliedTheme = useRef(state.theme);
  useEffect(() => {
    if (state.theme === 'dark') document.documentElement.classList.add('dark');
    else document.documentElement.classList.remove('dark');
    if (appliedTheme.current === state.theme) return;
    appliedTheme.current = state.theme;
    try { window.localStorage.setItem('wp.theme', state.theme); } catch { /* private mode */ }
  }, [state.theme]);

  const openDialog = useCallback((name, props, opts) => dispatch({ type: 'OPEN_DIALOG', payload: { name, props, stack: !!opts?.stack } }), []);
  const closeDialog = useCallback(() => dispatch({ type: 'CLOSE_DIALOG' }), []);

  const value = {
    state,
    dispatch: historyDispatch,
    rawDispatch: dispatch,
    stateRef,
    getCurrentPage,
    getActiveMaster,
    getActiveObjects: getActiveObjectsList,
    getSelectedObjects,
    getObjectById,
    pxToUnit,
    unitToPx,
    PAGE_PRESETS,
    undo,
    redo,
    canUndo,
    canRedo,
    openDialog,
    closeDialog,
    hasUnsavedChanges: state.docRevision !== state.savedRevision,
  };

  return <EditorContext.Provider value={value}>{children}</EditorContext.Provider>;
};

export const useEditor = () => {
  const context = useContext(EditorContext);
  if (!context) throw new Error('useEditor must be used within an EditorProvider');
  return context;
};

export default EditorContext;
